import { Button, Col, Flex, Form, InputNumber, Modal, ModalProps, notification, Radio, Row, Select, Typography } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import FanOperationType from "src/consts/FanOperationType";
import GrainType from "src/consts/GrainType";
import FanRunOperationsSettingsDTO from "src/models/FanRunOperationsSettingsDTO";
import PremierPlusBinDTO from "src/models/PremierPlusBinDTO";
import PremierPlusSyncSettingsRequestDTO from "src/models/PremierPlusSyncSettingsRequestDTO";
import BinApiService from "src/api/BinApiService";
import { convertCelsiusToFahrenheit, convertFahrenheitToCelsius } from "src/utils/generalUtils";
import { useCelsiusContext } from "../useCelsiusContext";

const { Text } = Typography;

interface PremierPlusFanSettingsModalProps extends ModalProps{
    binDto: PremierPlusBinDTO | null;
    onClose: (result: any) => void; 
}

const PremierPlusFanSettingsModal : React.FC<PremierPlusFanSettingsModalProps> = (props) => {

    const [form] = Form.useForm<FanRunOperationsSettingsDTO>();
    const values = Form.useWatch([], form);
    
    const [selectedGrain, setSelectedGrain] = useState(GrainType.Corn);
    const handleChange = (value: GrainType) => {
        setSelectedGrain(value);
        form.setFieldValue('grainType', value); 
    };

    const { useCelsius } = useCelsiusContext();
    
    const autoGenTextStyle = {
        fontSize: "12px",
        fontWeight: "bold",
        fontFamily: 'monospace'
    };

    useEffect( () => {
        if(form.isFieldsTouched()){
            return;
        }

        if(props.binDto?.settings?.fanRunSettings != null){
            form.setFieldsValue(props?.binDto?.settings?.fanRunSettings);
        }

        if(props?.binDto?.settings?.fanRunSettings?.fanOperationType === FanOperationType.Off){
            form.setFieldValue('fanOperationType', FanOperationType.On)
        }
        if(props?.binDto?.settings?.fanRunSettings?.grainType != null){
            handleChange(props?.binDto?.settings?.fanRunSettings?.grainType); 
        }
        if(useCelsius){
            form.setFieldValue('maxWeatherTemperatureF', convertFahrenheitToCelsius(props.binDto?.settings?.fanRunSettings?.maxWeatherTemperatureF));
            form.setFieldValue('minWeatherTemperatureF', convertFahrenheitToCelsius(props.binDto?.settings?.fanRunSettings?.minWeatherTemperatureF));
        }
    }, [props?.binDto, useCelsius]);

    const handleClose = () => {
        props.onClose(null);
    }

    const onFinish = async (data: FanRunOperationsSettingsDTO) => {
        data.grainType = selectedGrain;
        
        if(values.fanOperationType === FanOperationType.Automatic){
            if( (values.useFanTimer == null || values.useFanTimer === false) && (values.useEmcRange == null || values.useEmcRange === false) 
                && (values.useTempRange == null || values.useTempRange === false) ) {
                    notification.error({message: "To use Auto, you must have at least 1 automation used"});
                    return;
            }
        }

        if(data.useFanTimer){
            if(data.runFanMinutes <= 0 && data.runFanHours <=0){
                notification.error({ message: 'Running with fan timers can not be 0 minutes and hours' });
                return;
            }
        }

        if(data.useEmcRange){
            if(data.maxWeatherMc <= 0 && data.minWeatherMc <= 0){
                notification.error({ message: 'Running with EMC range can not be 0 for min and max' });
                return;
            }

            if(!(data.maxWeatherMc > data.minWeatherMc)){
                notification.error({ message: 'Minimum weather MC can not be more than maximum weather MC setpoint' });
                return;
            }
        }

        if(data.useTempRange){
            if(data.maxWeatherTemperatureF <= 0 && data.minWeatherTemperatureF <= 0){
                notification.error({ message: 'Running with temp range can not be 0 for min and max' });
                return;
            }

            if(!(data.maxWeatherTemperatureF > data.minWeatherTemperatureF)){
                notification.error({ message: 'Minimum weather temp can not be more than maximum weather temp setpoint' });
                return;
            }
        }

        try{

            if(props.binDto?.deviceId == null){
                return;
            }

            if(data.useTempRange && useCelsius){
                data.minWeatherTemperatureF = convertCelsiusToFahrenheit(values.minWeatherTemperatureF)!;
                data.maxWeatherTemperatureF = convertCelsiusToFahrenheit(values.maxWeatherTemperatureF)!;
            }

            const request : PremierPlusSyncSettingsRequestDTO = {
                azureDeviceId: props.binDto?.deviceId,
                settingsGuid: null,
                isWebRequest: true,
                generalSystemSettings: null,
                dryingOperationsSettings: null,
                grainMovingSettings: null,
                fanRunSettings: data
            }
            
            const result = await BinApiService.premierPlus_SetSettings(request);
            notification.success({ message: 'Settings successfully set'});
            props.onClose(null);

        }catch(err){
            notification.error({message: 'Could not set drying settings'});
        }

        props.onClose(null);
    }

    const initalSettings = useMemo (() => {
        return props.binDto?.settings?.fanRunSettings;
    }, [props.binDto]);

    return(
        <Modal
        {...props}
        title={<p>Fan Settings</p>}
        destroyOnClose
        footer={null}
        >  
            <Flex
            vertical
            gap="middle"
            style={{ height: "100%", maxWidth: "1200px", width: "100%" }}
            >
                <Form
                form={form}
                onFinish={onFinish}
                initialValues={initalSettings ?? {} as FanRunOperationsSettingsDTO}
                >
                    <Form.Item
                    name={'fanOperationType'}
                    >
                        <Radio.Group
                        optionType={'button'}
                        buttonStyle={'solid'}
                        block
                        >
                            <Radio value={FanOperationType.On}>ON</Radio>
                            <Radio value={FanOperationType.Automatic}>AUTO</Radio>
                        </Radio.Group>
                    </Form.Item>

                    {values?.fanOperationType === FanOperationType.Automatic && (
                        <>
                            <Text>Select Grain Type</Text>
                            <Form name={"grainType"}>
                                <Select
                                value={selectedGrain}
                                onChange={handleChange}
                                style={{ width: "400px" }}
                                >
                                    <Select.Option value={GrainType.Corn}>
                                        <Text>Corn</Text>
                                    </Select.Option>
                                    <Select.Option value={GrainType.Soybeans}>
                                        <Text>Soybeans</Text>
                                    </Select.Option>
                                    <Select.Option value={GrainType.Wheat}>
                                        <Text>Wheat</Text>
                                    </Select.Option>
                                </Select>
                            </Form>
                            <br/>
                            <Row gutter={24}>
                                <Col span={10}>
                                    <Text>Use EMC Set Point</Text>
                                    <Form.Item name={"useEmcRange"} initialValue={false}>
                                        <Radio.Group
                                        buttonStyle={"solid"}
                                        // style={{ gap: "12px" }}
                                        block
                                        defaultValue={false}
                                        optionType="button"
                                        
                                        >
                                        <Radio value={false}>NO</Radio>
                                        <Radio value={true}>YES</Radio>
                                        </Radio.Group>
                                    </Form.Item>
                                </Col>
                                {values?.useEmcRange && (
                                <Col span={12}>
                                    <Text>Enter EMC Range (%)</Text>
                                    <Row gutter={12}>
                                        <Col span={12}>
                                            <Form.Item
                                            name={"minWeatherMc"}
                                            required={false}
                                            rules={[{
                                                required: true, message:''
                                            }]}
                                            >
                                            <InputNumber
                                                controls={false}
                                                inputMode="decimal"
                                                max={100}
                                                min={0}
                                                placeholder="Min"
                                                suffix='min'
                                            />
                                            </Form.Item>
                                        </Col>

                                        <Col span={12}>
                                            <Form.Item
                                            name={"maxWeatherMc"}
                                            required={false}
                                            rules={[{
                                                required: true, message:''
                                            }]}
                                            >
                                            <InputNumber
                                                controls={false}
                                                inputMode="decimal"
                                                max={100}
                                                min={0}
                                                placeholder="Max"
                                                suffix='max'
                                            />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                </Col>
                                )}
                            </Row>
                            <br/>
                            <Row gutter={24}>
                                <Col span={10}>
                                <Text>Use Temp Set Point</Text>
                                <Form.Item name={"useTempRange"} initialValue={false}>
                                    <Radio.Group
                                    buttonStyle={"solid"}
                                    // style={{ gap: "12px" }}
                                    block
                                    defaultValue={false}
                                    optionType="button"
                                    >
                                    <Radio value={false}>NO</Radio>
                                    <Radio value={true}>YES</Radio>
                                    </Radio.Group>
                                </Form.Item>
                                </Col>

                                {values?.useTempRange && (
                                <Col span={12}>
                                    <Text>{useCelsius ? 'Enter Temp Range (°C)' : 'Enter Temp Range (°F)'}</Text>
                                    <Row gutter={12}>
                                    <Col span={12}>
                                        <Form.Item
                                        name={"minWeatherTemperatureF"}
                                        required={false}
                                        rules={[{
                                            required: true, message:''
                                        }]}
                                        >
                                        <InputNumber
                                            controls={false}
                                            inputMode="decimal"
                                            max={200}
                                            min={0}
                                            placeholder="Min"
                                            suffix='min'
                                        />
                                        </Form.Item>
                                    </Col>

                                    <Col span={12}>
                                        <Form.Item
                                        name={"maxWeatherTemperatureF"}
                                        required={false}
                                        rules={[{
                                            required: true, message:''
                                        }]}
                                        >
                                        <InputNumber
                                            controls={false}
                                            inputMode="decimal"
                                            max={200}
                                            min={0}
                                            placeholder="Max"
                                            suffix='max'
                                        />
                                        </Form.Item>
                                    </Col>
                                    </Row>
                                </Col>
                                )}
                            </Row>
                            <br/>

                            <Row gutter={24}>
                                <Col span={10}>
                                    <Text>Use Fan Timer</Text>
                                    <Form.Item name={"useFanTimer"} initialValue={false}>
                                        <Radio.Group
                                        buttonStyle={"solid"}
                                        defaultValue={false}
                                        // style={{ gap: "12px" }}
                                        block
                                        optionType="button"
                                        >
                                            <Radio value={false}>NO</Radio>
                                            <Radio value={true}>YES</Radio>
                                        </Radio.Group>
                                    </Form.Item>
                                </Col>
                                {values?.useFanTimer && (
                                <Col span={12}>
                                    <Text>Enter Duration</Text>
                                    <Row gutter={12}>
                                        <Col span={12}>
                                            <Form.Item
                                            name={"runFanHours"}
                                            required={false}
                                            rules={[{
                                                required: true, message:''
                                            }]}
                                            >
                                                <InputNumber
                                                controls={false}
                                                max={999}
                                                min={0}
                                                inputMode="decimal"
                                                placeholder="Hours"
                                                suffix='hr'
                                                />
                                            </Form.Item>
                                        </Col>

                                        <Col span={12}>
                                            <Form.Item
                                            name="runFanMinutes"
                                            dependencies={["useFanTimer"]}
                                            required={false}
                                            rules={[{
                                                required: true, message:''
                                            }]}
                                            >
                                                <InputNumber
                                                controls={false}
                                                inputMode="decimal"
                                                max={999}
                                                min={0}
                                                placeholder="Minutes"
                                                suffix='min'
                                                />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                </Col>
                                )}
                            </Row>

                        </>
                    )}

                    <br/>
                    
                    <span style={autoGenTextStyle}>
                        {values?.fanOperationType == FanOperationType.On &&
                        "Fan(s) will turn on when settings are submitted."}

                        {values?.fanOperationType == FanOperationType.Automatic &&
                        `Selected Grain Type: ${selectedGrain}.`}
                        {values?.fanOperationType == FanOperationType.Automatic &&
                        values?.useFanTimer &&
                        values?.runFanHours != null &&
                        values?.runFanMinutes != null &&
                        `Fans will run for ${values?.runFanHours}:${values?.runFanMinutes.toString().padStart(2, "0")}.`}
                        {values?.fanOperationType == FanOperationType.Automatic &&
                        values?.useEmcRange &&
                        values?.minWeatherMc != null &&
                        values?.maxWeatherMc != null &&
                        `EMC setpoints: ${values?.minWeatherMc}% - ${values?.maxWeatherMc}%.`}
                        {
                        values?.fanOperationType == FanOperationType.Automatic &&
                        values?.useTempRange &&
                        values?.minWeatherTemperatureF != null &&
                        values?.maxWeatherTemperatureF != null 
                        // &&  `Temperature setpoints: ${values?.minWeatherTemperatureF} - ${values?.maxWeatherTemperatureF}${useCelsius ? '°C' : '°F'}.`
                        }
                    </span>
                    <br/>
                    <Row justify={'end'} gutter={12}>
                        <Col>
                            <Button onClick={handleClose}>Cancel</Button>
                        </Col>
                        <Col>
                            <Button htmlType='submit' type='primary'>Submit</Button>
                        </Col>
                    </Row>
                </Form>
            </Flex>
        </Modal>
    )
}

export default PremierPlusFanSettingsModal;